import React, { useRef, useState } from "react";
import { Plus, Trash2, Loader2, ChevronLeft, ChevronRight, X, Pencil, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { L } from "@/components/ideaos/shared";
import { uploadFile } from "@/components/canvas/CanvasToolbar";
import { declareOrigin } from "@/components/OriginDeclaration";
import { fileUrl } from "@/components/canvas/canvasLib";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function Caption({ m, canEdit, onSave }) {
  const { lang } = useLang();
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(m.caption || "");
  if (editing) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); onSave(v); setEditing(false); }} className="mt-1.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input autoFocus value={v} maxLength={200} onChange={(e) => setV(e.target.value)} placeholder={L(lang, "Didascalia", "Caption")} className="h-8 min-w-0 flex-1 rounded-full border border-slate-300 px-3 text-xs outline-none focus:border-[#7c3aed]" data-testid={`mockup-caption-input-${m.id}`} />
        <button type="submit" className="h-8 rounded-full bg-[#0b0f1a] px-3 text-[11px] font-semibold text-white" data-testid={`mockup-caption-save-${m.id}`}>OK</button>
      </form>
    );
  }
  return (
    <p className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500" data-testid={`mockup-caption-${m.id}`}>
      <span className="truncate">{m.caption || (canEdit ? L(lang, "Aggiungi didascalia", "Add caption") : "")}</span>
      {canEdit && <button type="button" aria-label="edit caption" onClick={(e) => { e.stopPropagation(); setEditing(true); }} className="shrink-0 text-slate-400 hover:text-[#0b0f1a]" data-testid={`mockup-caption-edit-${m.id}`}><Pencil size={10} /></button>}
    </p>
  );
}

/** Overview gallery of product/service mockups (project.mockups): multi-upload, captions, lightbox, delete. */
export function IdeaMockups({ project, canEdit, onChanged, embedded = false }) {
  const { lang } = useLang();
  const list = project.mockups || [];
  const [busy, setBusy] = useState(0);
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  const add = async (files) => {
    const imgs = [...files].filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return;
    setBusy(imgs.length);
    const origin = await declareOrigin(imgs.length);
    let next = list;
    for (const f of imgs) {
      try { const up = await uploadFile(f, { scope: "asset", origin }); const { data } = await api.post(`/projects/${project.id}/mockups`, { url: up.url, name: up.name }); next = [...next, data]; onChanged?.(next); }
      catch (e) { if (!e?.provenanceRejected) toast.error(e.response?.data?.detail || e.message); }
      setBusy((n) => n - 1);
    }
  };
  const remove = async (m) => {
    try { await api.delete(`/projects/${project.id}/mockups/${m.id}`); onChanged?.(list.filter((x) => x.id !== m.id)); setOpen(null); }
    catch (e) { if (!e?.provenanceRejected) toast.error(e.response?.data?.detail || e.message); }
  };
  const caption = async (m, c) => {
    try { await api.patch(`/projects/${project.id}/mockups/${m.id}`, { caption: c }); onChanged?.(list.map((x) => (x.id === m.id ? { ...x, caption: c } : x))); }
    catch (e) { if (!e?.provenanceRejected) toast.error(e.response?.data?.detail || e.message); }
  };
  if (!list.length && !canEdit) return null;
  const idx = open != null ? list.findIndex((m) => m.id === open) : -1;
  const cur = idx >= 0 ? list[idx] : null;
  return (
    <section className={embedded ? "mt-5 border-t border-dashed border-slate-200 pt-4" : "rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 md:col-span-2"} data-testid="idea-mockups">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className={embedded ? "text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500" : "cpenz-overline"}>{L(lang, "Mockup prodotto / servizio", "Product / service mockups")}</span>
          <p className="mt-1 text-xs text-slate-500">{L(lang, "Schermate, bozzetti, rendering: come si presenta l'idea nel mondo reale.", "Screens, sketches, renders: how the idea looks in the real world.")}</p>
        </div>
        {canEdit && (
          <>
            <input ref={ref} type="file" accept="image/*" multiple className="hidden" data-testid="mockups-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
            <button type="button" onClick={() => ref.current?.click()} disabled={busy > 0} className="inline-flex h-9 items-center gap-1 rounded-full bg-[#0b0f1a] px-4 text-xs font-semibold text-white disabled:opacity-60" data-testid="mockups-add-btn">{busy > 0 ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {L(lang, "Aggiungi immagini", "Add images")}{busy > 0 && ` · ${busy}`}</button>
          </>
        )}
      </div>
      {list.length === 0 ? (
        <button type="button" onClick={() => ref.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
          className="mt-4 flex w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#7c3aed]/50 bg-[#f7f5ff] py-8 text-sm text-[#0b0f1a] transition-colors hover:border-[#7c3aed] hover:bg-[#ede9fe]" data-testid="mockups-empty">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#7c3aed] text-white"><Upload size={18} /></span>
          <span className="mt-2 font-semibold">{L(lang, "Carica il primo mockup", "Upload the first mockup")}</span>
          <span className="mt-0.5 text-[11px] text-slate-500">{L(lang, "Trascina qui le immagini o tocca per sceglierle · PNG, JPG, WEBP", "Drop images here or tap to pick them · PNG, JPG, WEBP")}</span>
        </button>
      ) : (
        <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="mockups-grid">
          {list.map((m) => (
            <li key={m.id} className="min-w-0" data-testid={`mockup-${m.id}`}>
              <button type="button" onClick={() => setOpen(m.id)} className="block aspect-[4/3] w-full overflow-hidden rounded-2xl bg-[#f4f6fa] ring-1 ring-slate-200 transition-transform hover:-translate-y-0.5" data-testid={`mockup-open-${m.id}`}>
                <img src={fileUrl(m.url)} alt={m.caption || m.name || "mockup"} className="h-full w-full object-cover" loading="lazy" />
              </button>
              <Caption m={m} canEdit={canEdit} onSave={(c) => caption(m, c)} />
            </li>
          ))}
          {canEdit && <li className="min-w-0"><button type="button" onClick={() => ref.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }} className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-[#7c3aed]/40 bg-[#f7f5ff] text-xs font-semibold text-[#7c3aed] transition-colors hover:border-[#7c3aed] hover:bg-[#ede9fe]" data-testid="mockups-add-tile"><Plus size={20} /> {L(lang, "Aggiungi", "Add")}</button></li>}
        </ul>
      )}
      <Dialog open={!!cur} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl rounded-3xl bg-[#0b0f1a] p-3 text-white sm:p-4 [&>button]:hidden" data-testid="mockup-lightbox">
          {cur && (
            <>
              <DialogTitle className="sr-only">{cur.caption || cur.name || "mockup"}</DialogTitle>
              <div className="flex items-center justify-between gap-2">
                <p className="min-w-0 truncate text-sm text-slate-200" data-testid="mockup-lightbox-caption">{cur.caption || cur.name} <span className="text-slate-500">· {idx + 1}/{list.length}</span></p>
                <div className="flex items-center gap-1">
                  {canEdit && <button type="button" aria-label="delete" onClick={() => remove(cur)} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-red-500" data-testid="mockup-delete"><Trash2 size={14} /></button>}
                  <button type="button" aria-label="close" onClick={() => setOpen(null)} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-white/20" data-testid="mockup-lightbox-close"><X size={16} /></button>
                </div>
              </div>
              <div className="relative mt-3 flex items-center justify-center">
                <img src={fileUrl(cur.url)} alt={cur.caption || ""} className="max-h-[70vh] w-auto max-w-full rounded-2xl object-contain" />
                {list.length > 1 && (
                  <>
                    <button type="button" aria-label="prev" onClick={() => setOpen(list[(idx - 1 + list.length) % list.length].id)} className="absolute left-1 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 hover:bg-black/70" data-testid="mockup-prev"><ChevronLeft size={18} /></button>
                    <button type="button" aria-label="next" onClick={() => setOpen(list[(idx + 1) % list.length].id)} className="absolute right-1 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 hover:bg-black/70" data-testid="mockup-next"><ChevronRight size={18} /></button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
